import { FormControl, FormHelperText, Radio, RadioGroup, FormControlLabel, Box, Alert } from '@mui/material'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { CampaignInputComponent, CampaignInputComponentProps } from './CampaignInputComponent'
import { FormatVersion } from '~/generated/graphql'
import { z } from 'zod'
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DateTime } from 'luxon'
import { DatePicker } from '@mui/x-date-pickers-pro'
import { useState } from 'react'
import { getNowJst } from '~/lib/getNowJst'
import { getJstEndOfApril30Year2026, getSelectableDateRange } from '~/lib/getSelectableDateRange'
import WarningIcon from '@mui/icons-material/Warning'

export type ValidateOfferStartedOptions = {
  restrictOfferStartedForXCampaign?: boolean
  /** テスト・Story 用。未指定時は実行時の JST 現在 */
  nowJst?: DateTime
}

const offerStartedTooBigMessageForX =
  'X（旧Twitter）を募集対象に含む場合、公開希望日は2026年4月30日までに指定してください。'

export const PublishType = {
  Immediate: 'immediate',
  Scheduled: 'scheduled',
} as const

export const MaxSelectableDateOptions = {
  label: '3週間',
  value: 21,
} as const

export const offerStartedSchemaFields = {
  publishType: z.enum([PublishType.Immediate, PublishType.Scheduled]),
  offerStarted: z.union([z.date(), z.null()]).optional(),
}

export const offerStartedSchema = z.object(offerStartedSchemaFields)

function getOfferStartedSelectableBounds(
  nowJst: DateTime,
  restrictOfferStartedForXCampaign: boolean,
): { startDate: DateTime; maxDate: DateTime } {
  const { startDate, maxDate: rangeMax } = getSelectableDateRange(nowJst, 1, MaxSelectableDateOptions.value)
  const xDeadlineEnd = getJstEndOfApril30Year2026()
  const maxDate = restrictOfferStartedForXCampaign ? DateTime.min(rangeMax, xDeadlineEnd) : rangeMax
  return { startDate, maxDate }
}

export const validateOfferStarted = (
  data: z.infer<z.ZodObject<typeof offerStartedSchemaFields>>,
  ctx: z.RefinementCtx,
  options?: ValidateOfferStartedOptions,
) => {
  const { publishType, offerStarted } = data

  const nowJst = options?.nowJst ?? getNowJst()
  const restrict = options?.restrictOfferStartedForXCampaign === true
  const { startDate, maxDate } = getOfferStartedSelectableBounds(nowJst, restrict)
  const selectableStart = startDate.toJSDate()
  const maxSelectable = maxDate.toJSDate()

  if (publishType === PublishType.Scheduled) {
    if (!offerStarted) {
      ctx.addIssue({
        path: ['offerStarted'],
        code: z.ZodIssueCode.custom,
        message: '公開希望日を入力してください',
      })
      return
    }

    if (offerStarted < selectableStart) {
      ctx.addIssue({
        path: ['offerStarted'],
        code: z.ZodIssueCode.too_small,
        type: 'date',
        inclusive: true,
        minimum: selectableStart.getTime(),
        message: '翌日以降の日付を入力してください',
      })
    }

    if (offerStarted > maxSelectable) {
      ctx.addIssue({
        path: ['offerStarted'],
        code: z.ZodIssueCode.too_big,
        type: 'date',
        inclusive: true,
        maximum: maxSelectable.getTime(),
        message: restrict ? offerStartedTooBigMessageForX : `翌日から${MaxSelectableDateOptions.label}以内の日付を入力してください`,
      })
    }
  }
}

type OfferStartedDatePickerProps = CampaignInputComponentProps & {
  /** X（旧Twitter）を募集する場合、公開希望日の上限を2026年4/30（JST）までに抑える */
  restrictOfferStartedForXCampaign?: boolean
}

export const OfferStartedDatePicker = ({
  description,
  tooltip,
  restrictOfferStartedForXCampaign = false,
}: OfferStartedDatePickerProps) => {
  const { control, setValue } = useFormContext<z.infer<typeof offerStartedSchema>>()
  const [pickerOpen, setPickerOpen] = useState(false)

  const watchPublishType = useWatch({ name: 'publishType' })
  const watchOfferStarted = useWatch({ name: 'offerStarted' })

  const { startDate, maxDate } = getOfferStartedSelectableBounds(getNowJst(), restrictOfferStartedForXCampaign)

  return (
    <CampaignInputComponent
      title="公開希望日"
      description={description}
      tooltip={tooltip}
      compatibleFormatVersions={[FormatVersion.V4Campaign]}
    >
      <FormControl component="fieldset" fullWidth>
        <Controller
          name="publishType"
          control={control}
          render={({ field }) => (
            <RadioGroup
              row
              value={field.value}
              onChange={(e) => {
                const selected = e.target.value
                field.onChange(selected)
                if (selected === PublishType.Immediate) {
                  setValue('offerStarted', null)
                } else {
                  setValue('offerStarted', watchOfferStarted ?? startDate.toJSDate(), {
                    shouldDirty: true,
                  })
                }
              }}
            >
              <FormControlLabel
                value={PublishType.Immediate}
                control={<Radio />}
                label="承認後すぐに公開したい（推奨）"
              />
              <FormControlLabel value={PublishType.Scheduled} control={<Radio />} label="公開希望日を指定したい" />
            </RadioGroup>
          )}
        />

        {watchPublishType === PublishType.Scheduled && (
          <Box mt={2}>
            <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
              【こちらの機能は公開日を確約するものではありません。】
              <br />
              公開希望日は翌日から選択可能ですが、希望日までに審査が承認されなかった場合は、審査承認後すぐの公開となります。予めご了承ください。
            </Alert>
            <LocalizationProvider dateAdapter={AdapterLuxon} adapterLocale="ja">
              <Controller
                name="offerStarted"
                control={control}
                render={({ field, fieldState }) => {
                  return (
                    <DatePicker
                      {...field}
                      open={pickerOpen}
                      onOpen={() => setPickerOpen(true)}
                      onClose={() => setPickerOpen(false)}
                      value={field.value ? DateTime.fromJSDate(field.value, { zone: 'Asia/Tokyo' }) : startDate}
                      minDate={startDate}
                      maxDate={maxDate}
                      onChange={(date: DateTime | null) => {
                        if (date?.isValid) {
                          field.onChange(date.toJSDate())
                        } else {
                          field.onChange(null)
                        }
                      }}
                      onAccept={field.onBlur}
                      slotProps={{
                        textField: {
                          error: !!fieldState.error,
                          helperText: fieldState.error?.message,
                          fullWidth: true,
                          onBlur: field.onBlur,
                          onClick: () => setPickerOpen(true),
                          sx: {
                            '& .MuiPickersInputBase-sectionsContainer': {
                              padding: '8.5px 0',
                            },
                          },
                        },
                        openPickerButton: {
                          onClick: (e: React.MouseEvent) => {
                            e.stopPropagation()
                            setPickerOpen(true)
                          },
                        },
                      }}
                    />
                  )
                }}
              />
            </LocalizationProvider>
            <FormHelperText sx={{ mt: 1, ml: 0 }}>
              ※公開希望日は翌日から{MaxSelectableDateOptions.label}以内で選択できます。
              {restrictOfferStartedForXCampaign && (
                <>X（旧Twitter）を募集対象に含む場合は、2026年4月30日までに限られます。</>
              )}
            </FormHelperText>
          </Box>
        )}
      </FormControl>
    </CampaignInputComponent>
  )
}
